import Alert from "@pathwright/ui/src/components/alert/Alert"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Button from "@pathwright/ui/src/components/button/Button"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import RichTextInput from "@pathwright/ui/src/components/form/form-richtext/RichTextInput"
import TextInput from "@pathwright/ui/src/components/form/form-text-input/TextInput"
import TextInputVariableSelector from "@pathwright/ui/src/components/form/form-text-input/TextInputVariableSelector"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { stripHTML } from "@pathwright/ui/src/components/text/utils"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import { SECONDARY_GRAY } from "@pathwright/ui/src/components/utils/colors"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import get from "lodash/get"
import moment from "moment"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { Field, Form, Submit, withForm } from "react-connect-form-forked"
import styled from "styled-components"
import { usePathItemQuery } from "../../api/generated"
import SelectInput from "../../lib/SelectInput/SelectInput"
import PathScheduleLabel from "../../path/ui/PathScheduleLabel"
import useRestAPIGetQuery from "../../rest-api/graphql/useRestAPIGetQuery"
import MESSAGE_QUERY from "../graphql/message-query"
import {
  getDefaultReminderSubjectBody,
  getInitialMessageValues,
  getMessageOutput,
  getMessageThresholdMinutesForUnit,
  useDeleteMessage,
  useSubmitMessage
} from "../utils"

const StyledFauxFieldRow = styled.div`
  display: flex;
  align-items: baseline;
  > div {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  label {
    width: 75px;
    flex-shrink: 0;
    color: ${SECONDARY_GRAY};
  }
  .TextInput,
  .SelectInput {
    margin-right: 4px;
  }
`

const StyledFauxField = styled(Field)`
  display: none;
`

const StyledSelect = styled(SelectInput)`
  .Select {
    width: 250px;
  }
`

const StyledTextInputVariableSelector = styled(TextInputVariableSelector)`
  padding: 0;
  > div {
    margin-bottom: 0;
  }
  input {
    background-color: white;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .TextInput input[placeholder] {
    color: #ccc;
  }
`

const ThresholdTextInput = styled(TextInput)`
  .TextInput__input input {
    line-height: 24px;
  }
`

const StyledTooltip = styled(Tooltip)`
  display: flex !important;
  padding-left: 5px;
  flex-direction: column;
  justify-content: center;

  .Tooltip {
    max-width: 65vw;
    white-space: normal;
    /* padding: 4px 8px; */
  }
`

const StyledRichTextInput = styled(RichTextInput)`
  padding: 0;
  > div {
    margin-top: 0;
  }
  .ql-editor {
    border-top-color: transparent;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    min-height: 100px;
    background-color: white;
  }
  .ql-editor.ql-blank::before {
    color: #ccc;
    font-style: normal;
  }
`

const StyledStepName = styled(Button)`
  .Button__content-wrapper {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
  }
`

// responsible for updating default subject / body values when threshold_timing changes
const HandleDefaultSubjectBody = withForm(
  ({ pathItem, stepDateField, reminderDefaults, formState, formActions }) => {
    const currentThresholdTimingRef = React.useRef(
      formState.value.threshold_timing
    )

    const getSubjectBody = (thresholdTiming) => {
      // shape data to be expected message shape
      const nextReminder = getMessageOutput({
        ...formState.value,
        threshold_timing: thresholdTiming
      })

      // get default subject and body for the pathItem with the pathItem's next threshold_minutes
      const [subject, body] = getDefaultReminderSubjectBody(
        pathItem,
        stepDateField,
        nextReminder,
        reminderDefaults
      )
      return [subject, body]
    }

    React.useEffect(() => {
      // not handling initial default, just the default when threshold_timing changes from the default value
      if (currentThresholdTimingRef.current) {
        const [currentSubject, currentBody] = getSubjectBody(
          currentThresholdTimingRef.current
        )

        if (
          formState.value.subject === currentSubject &&
          stripHTML(formState.value.body) === currentBody
        ) {
          const [nextSubject, nextBody] = getSubjectBody(
            formState.value.threshold_timing
          )

          // reset subject and body field values to defaults for the form's threshold_timing
          formActions.changeField("subject", nextSubject, true)
          formActions.changeField("body", nextBody, true)
        }
      }

      currentThresholdTimingRef.current = formState.value.threshold_timing
    }, [formState.value.threshold_timing])

    return null
  }
)

const ReminderFormCard = ({ card, id, stepDateField, cohortId, sourceId }) => {
  const handleSubmit = useSubmitMessage(
    {
      cohortId,
      sourceId
    },
    card.onClose
  )
  const handleDelete = useDeleteMessage(
    {
      cohortId,
      sourceId
    },
    card.onClose
  )
  const [isDeleting, setIsDeleting] = useState(false)
  const { t, tc, tca } = useTranslate()

  const pathItemQuery = usePathItemQuery({
    variables: {
      cohort_id: cohortId,
      source_id: sourceId
    }
  })

  const messageQuery = useQuery(MESSAGE_QUERY, {
    skip: !id,
    variables: {
      id: id,
      message_context: {
        cohort_id: cohortId,
        source_id: sourceId
      }
    }
  })

  const reminderDefaultsQuery = useRestAPIGetQuery("/reminder/defaults/")
  const reminderVariablesQuery = useRestAPIGetQuery("/reminder/variables/")

  const pathItem = get(pathItemQuery, "data.pathItem")
  const message = get(messageQuery, "data.message")
  const reminderDefaults = get(reminderDefaultsQuery, "data.restAPIGet")
  const reminderVariables = get(reminderVariablesQuery, "data.restAPIGet")

  stepDateField =
    get(messageQuery, "data.message.based_on_step_date") || stepDateField

  return (
    <Card
      card={card}
      title={!id ? tca("add reminder") : message ? tca("edit reminder") : ""}
      size="small"
    >
      {messageQuery.error && <Alert error={messageQuery.error} />}
      {messageQuery.loading ||
      pathItemQuery.loading ||
      reminderDefaultsQuery.loading ? (
        <LoadingCircle />
      ) : (id && !message) || !pathItem ? (
        <BlankSlate
          icons={null}
          heading={t("This reminder may have been deleted or does not exist.")}
          primaryAction={{
            children: tc("ok"),
            onClick: card.onClose
          }}
        />
      ) : isDeleting ? (
        <BlankSlate
          icons={null}
          heading={t("Are you sure you want to delete this reminder?")}
          primaryAction={{
            children: tc("yes, delete"),
            onClick: (e) => {
              e.preventDefault()
              handleDelete(id)
            }
          }}
          secondaryAction={{
            children: tc("cancel"),
            onClick: () => setIsDeleting(false)
          }}
        />
      ) : !stepDateField || !pathItem[stepDateField] ? (
        <BlankSlate
          icons={null}
          icon="meh"
          heading={t("Cannot add a reminder.")}
          body={t("This path item must first be scheduled.")}
          primaryAction={{
            children: tc("schedule"),
            onClick: card.onClose
          }}
        />
      ) : (
        <Form
          onSubmit={(values) => handleSubmit(id, getMessageOutput(values))}
          initialValue={getInitialMessageValues(
            message,
            pathItem,
            stepDateField,
            reminderDefaults
          )}
          onDirty={() => message && card.setCardDirty(true)}
          onPristine={() => message && card.setCardDirty(false)}
          render={({ onSubmit, ...formState }) => {
            const showNotificationTooltip =
              formState.value.channels === "notification" ||
              formState.value.channels === "email,notification"

            const messageSendsDate = moment(pathItem[stepDateField])
              .add(
                "minutes",
                getMessageThresholdMinutesForUnit(
                  formState.value.threshold_num,
                  formState.value.threshold_unit,
                  formState.value.threshold_timing
                )
              )
              .toDate()

            return (
              <form onSubmit={onSubmit}>
                <Submit styleType="primary" component={SubmitButton}>
                  {tc("save")}
                </Submit>
                {formState.submitFailure && (
                  <Alert error={formState.submitFailure} />
                )}
                {formState.errors && Object.keys(formState.errors).length
                  ? Object.keys(formState.errors).map((e) => {
                      const errorSet = formState.errors[e]
                      return errorSet.map((errorMessage) => (
                        <Alert error={errorMessage} />
                      ))
                    })
                  : null}
                <CardBlock style={{ position: "relative" }}>
                  <StyledFauxFieldRow>
                    <label>{tc("remind")}</label>
                    <div>
                      {/* hidden Field as a placeholder for threshold_datefield value */}
                      <StyledFauxField
                        name="threshold_datefield"
                        type={"custom"}
                      />
                      <Field
                        name="threshold_num"
                        type="number"
                        component={ThresholdTextInput}
                        placeholder="15"
                        style={{ width: 55 }}
                        hideStatus // hiding status as the inline input's validator message gets squished =/
                        validators={[
                          (value) => {
                            if (!value) return "Required"
                            if (parseInt(value) <= 0)
                              return "Must be positive number"
                            if (!Number.isInteger(Number(value)))
                              return "Please use an integer (whole number)"
                          }
                        ]}
                      />
                      <Field
                        name="threshold_unit"
                        type={"custom"}
                        component={SelectInput}
                        simpleValue
                        style={{ width: 75 }}
                        options={[
                          { value: "min", label: t("min") },
                          { value: "hour", label: t("hour") },
                          { value: "day", label: t("day") }
                        ].map((x) => {
                          const n = Number(formState.value.threshold_num) || 0
                          // TODO: translate label
                          return { ...x, label: pluralize(x.label, n) }
                        })}
                      />
                      <Field
                        name="threshold_timing"
                        type={"custom"}
                        inline={true}
                        component={SelectInput}
                        simpleValue
                        style={{ width: 80 }}
                        options={[
                          { value: "before", label: t("before") },
                          {
                            value: "after",
                            label: t("after")
                          }
                        ]}
                      />
                    </div>
                  </StyledFauxFieldRow>
                  <small
                    style={{
                      display: "block",
                      color: SECONDARY_GRAY,
                      marginBottom: "7px"
                    }}
                  >
                    {message &&
                    message.sent_date &&
                    // NOTE: likely need to remove this last conditional as changing the send_date will not actually cause this reminder to ever be sent again (current behavior)
                    moment(message.send_date).isSame(moment(messageSendsDate))
                      ? tc("sent on {{ date }}", {
                          date: moment(messageSendsDate).format("LLL")
                        })
                      : tc("sends on {{ date }}", {
                          date: moment(messageSendsDate).format("LLL")
                        })}
                  </small>
                  <div
                    style={{ display: "flex", justifyContent: "flex-start" }}
                  >
                    <Field
                      name="channels"
                      type={"custom"}
                      label={tc("send as")}
                      labelWidth={75}
                      component={StyledSelect}
                      simpleValue
                      options={[
                        { value: "email", label: tc("email notification") },
                        {
                          value: "notification",
                          label: tc("app notification")
                        },
                        {
                          value: "email,notification",
                          label: tc("app + email notification")
                        }
                      ]}
                    />
                    {showNotificationTooltip ? (
                      <StyledTooltip
                        placement="left"
                        alignment="center"
                        transition="transition.slideRightIn"
                        title={t(
                          "*App notification requires learners to have the iOS app installed, and notifications enabled."
                        )}
                      >
                        <Pathicon
                          icon="info-circle"
                          style={{ color: "#8b9898" }}
                        />
                      </StyledTooltip>
                    ) : null}
                  </div>
                  <Field
                    name="recipient_filter"
                    type={"custom"}
                    label={tc("send to")}
                    labelWidth={75}
                    component={StyledSelect}
                    simpleValue
                    options={[
                      { value: "all", label: tc("all learners") },
                      {
                        value: "step_complete",
                        label: tc("all completed")
                      },
                      {
                        value: "step_incomplete",
                        label: tc("all incomplete")
                      }
                    ]}
                  />
                </CardBlock>
                <CardBlock
                  borderTop={false}
                  style={{
                    paddingTop: 15,
                    marginBottom: -10,
                    borderBottomLeftRadius: 10,
                    borderBottomRightRadius: 10,
                    backgroundColor: "whitesmoke "
                  }}
                >
                  <label
                    style={{
                      color: SECONDARY_GRAY,
                      marginBottom: 10,
                      display: "block",
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "pre"
                    }}
                  >
                    {tca("reminder message")}{" "}
                    <Tooltip
                      title={
                        <>
                          <p
                            style={{
                              whiteSpace: "normal",
                              marginTop: "0",
                              minWidth: "170px"
                            }}
                          >
                            You can personalize a Reminder's subject and message
                            using variables.
                          </p>
                          <a href="https://help.pathwright.com/en/articles/4428159-use-variables-in-custom-reminders">
                            Learn more
                          </a>
                        </>
                      }
                    >
                      <Pathicon icon="info-circle" />
                    </Tooltip>
                  </label>
                  {/* handles resetting default subject / body values when threshold_timing changes */}
                  <HandleDefaultSubjectBody
                    pathItem={pathItem}
                    stepDateField={stepDateField}
                    reminderDefaults={reminderDefaults}
                  />
                  <Field
                    name="subject"
                    type={"text"}
                    placeholder={tc("subject")}
                    variables={reminderVariables}
                    component={StyledTextInputVariableSelector}
                    required
                    hideStatus
                  />
                  <Field
                    name="body"
                    type={"text"}
                    placeholder={tc("body")}
                    variables={reminderVariables}
                    component={StyledRichTextInput}
                    required
                    hideStatus
                    validators={[
                      (value) => {
                        if (!stripHTML(value)) return "Required"
                      }
                    ]}
                  />
                  <small
                    style={{
                      display: "block",
                      color: SECONDARY_GRAY,
                      margin: "12px 0 7px"
                    }}
                  >
                    {t(`The following will be included in the email footer`)}:
                  </small>
                  <div
                    style={{
                      padding: "14px",
                      margin: "5px 0 10px",
                      backgroundColor: "white"
                    }}
                  >
                    <StyledStepName
                      style={{
                        marginBottom: "4px",
                        textDecoration: "underline",
                        textAlign: "left",
                        border: "none",
                        whiteSpace: "pre-wrap"
                      }}
                      styleType="text"
                      brand
                      interactive={false}
                    >
                      {pathItem.name}
                    </StyledStepName>
                    <br />
                    <small
                      style={{
                        marginTop: 4,
                        color: SECONDARY_GRAY,
                        display: "inline-block"
                      }}
                    >
                      <PathScheduleLabel pathItem={pathItem} showIcon />
                    </small>
                  </div>
                  {message ? (
                    <Button
                      styleType="card"
                      color="red"
                      onClick={(e) => {
                        e.preventDefault()
                        handleDelete(id)
                      }}
                    >
                      {tc("delete reminder")}
                    </Button>
                  ) : null}
                </CardBlock>
              </form>
            )
          }}
        />
      )}
    </Card>
  )
}

ReminderFormCard.displayName = "ReminderFormCard"

ReminderFormCard.propTypes = {
  stepDateField: PropTypes.oneOf(["starts", "due"])
}

ReminderFormCard.defaultProps = {
  stepDateField: "starts"
}

export default ReminderFormCard
